/**
 * Dashboard.jsx - User Dashboard Page
 * 
 * @description
 * Main dashboard for authenticated users to manage their QuickLinks profile.
 * Provides a tabbed interface for managing profile, links, and analytics.
 * 
 * @route /dashboard
 * @auth Required - Redirects to /signin if not authenticated
 * 
 * @features
 * - Profile management (name, bio, images)
 * - Custom links with add/edit/delete/reorder
 * - Analytics viewing
 * - Support ticket system
 * 
 * @tabs
 * - profile: User profile information and images
 * - links: Custom links management
 * - analytics: Visitor and engagement statistics
 * - tickets: Support ticket system
 * 
 * @stateManagement
 * - userData: Main user data object
 * - analyticsData: Analytics statistics
 * - activeTab: Currently selected tab
 * - theme: Dashboard theme (light/dark)
 * 
 * @tierFeatures
 * Free: Basic functionality with essential features
 * Pro: Enhanced analytics and advanced features
 * Enterprise: Full feature set with priority support
 * 
 * @dependencies
 * - API service for backend integration
 * - Toast component for notifications
 * - LoadingSpinner for loading states
 * - Analytics service for tracking
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SocialIcons } from '../components/SocialIcons';
import { getUserProfile, getAnalytics, getLinks, updateUserProfile, createLink, updateLink, deleteLink, isAuthenticated, logout, updateSocialLinks, getSocialLinks, updateUserTheme, getUserTheme, getFooterBranding, updateFooterBranding, resetFooterBranding, getSeoSettings, updateSeoSettings, resetSeoSettings } from '../services/api.js';
import { THEME_PRESETS } from '../data/themePresets';
import { LoadingButton, LoadingPage } from '../components/LoadingSpinner';
import { useToast } from '../components/Toast';
import MobileMenu, { MobileMenuButton } from '../components/MobileMenu';
import { analyticsService } from '../services/analytics';
import TicketSystem from '../components/TicketSystem';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import UserImageUploader from '../components/UserImageUploader';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Check URL parameter first, fallback to 'profile'
    return searchParams.get('tab') || 'profile';
  });
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [backendAvailable, setBackendAvailable] = useState(true);
  const [error, setError] = useState(null);
  const { showSuccess, showError, ToastContainer } = useToast();
  
  // Check authentication on mount
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/signin', { replace: true });
    }
  }, [navigate]);
  
  
  // Get user data from backend API
  const [userData, setUserData] = useState({
    id: null,
    name: '',
    email: '',
    username: '',
    bio: '',
    tier: 'free',
    links: [],
    socialLinks: {},
    theme: {
      preset: 'default',
      customCSS: '',
      customColors: {
        primary: '#6366f1',
        secondary: '#ec4899',
        background: '#ffffff'
      }
    },
    seo: {
      title: '',
      description: '',
      keywords: ''
    },
    footerBranding: {
      text: 'Hosted by Link Spaghetti ❤️'
    },
    profile_image: null,
    banner_image: null
  });
  const [analyticsData, setAnalyticsData] = useState({
    totalViews: 0,
    uniqueVisitors: 0,
    totalClicks: 0,
    conversionRate: 0,
    daily: [],
    sources: [],
    devices: {},
    links: [],
    geography: []
  });
  const [availableThemes, setAvailableThemes] = useState([]);
  const [isOfflineMode, setIsOfflineMode] = useState(false);

  // Helper function to handle API errors consistently
  const handleApiError = (error, fallbackMessage = 'An error occurred') => {
    // Check if it's an authentication error
    if (error && (error.status === 401 || error.message?.includes('unauthorized') || error.message?.includes('token'))) {
      logout();
      navigate('/signin', { replace: true });
      return;
    }
    
    showError(fallbackMessage);
  };

  // Separate data loading function
  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch user data from backend with graceful error handling
      let data = null;
      let analytics = null;
      let links = [];
      let socialLinks = {};
      let footerBranding = { text: 'Hosted by Link Spaghetti ❤️' };
      let seoSettings = {
        title: '',
        description: '',
        og_title: '',
        og_description: '',
        og_image: '',
        twitter_title: '',
        twitter_description: '',
        twitter_image: '',
        keywords: '',
        robots: 'index,follow'
      };
      let backendAvailable = true;

      try {
        // First try to get the user profile as it's critical
        data = await getUserProfile();
        if (import.meta.env.DEV) {
          console.log('📋 Dashboard initial userData:', {
            id: data?.id,
            name: data?.name,
            profileImage: data?.profileImage,
            bannerImage: data?.bannerImage,
            profile_image_url: data?.profile_image_url,
            banner_image_url: data?.banner_image_url,
            allFields: data ? Object.keys(data) : 'no data'
          });
        }
        
        // If we got the profile, fetch other data in parallel
        if (data) {
          // Check if getSocialLinks is defined before using it
          const getSocialLinksFunc = typeof getSocialLinks === 'function' 
            ? getSocialLinks 
            : () => {
                console.warn('getSocialLinks function not available, using fallback');
                const offlineData = localStorage.getItem('socialLinks');
                if (offlineData) {
                  try {
                    return Promise.resolve(JSON.parse(offlineData));
                  } catch (e) {
                    console.error('Error parsing offline social links:', e);
                  }
                }
                return Promise.resolve({
                  twitter: '',
                  instagram: '',
                  facebook: '',
                  linkedin: '',
                  github: '',
                  youtube: '',
                  tiktok: ''
                });
              };
          
          const [analyticsResult, linksResult, socialLinksResult, footerBrandingResult, seoSettingsResult] = await Promise.all([
            getAnalytics().catch(err => {
              if (import.meta.env.DEV) {
                console.warn('Analytics API not available:', err);
              }
              return null;
            }),
            getLinks().catch(err => {
              if (import.meta.env.DEV) {
                console.warn('Links API not available:', err);
              }
              return [];
            }),
            getSocialLinksFunc().catch(err => {
              if (import.meta.env.DEV) {
                console.warn('Social Links API not available:', err);
              }
              // Check if we have offline data
              const offlineData = localStorage.getItem('socialLinks');
              if (offlineData) {
                setIsOfflineMode(true);
                return JSON.parse(offlineData);
              }
              return {};
            }),
            getFooterBranding().catch(err => {
              if (import.meta.env.DEV) {
                console.warn('Footer Branding API not available:', err);
              }
              return { text: 'Hosted by Link Spaghetti ❤️' };
            }),
            getSeoSettings().catch(err => {
              if (import.meta.env.DEV) {
                console.warn('SEO Settings API not available:', err);
              }
              return {
                title: '',
                description: '',
                og_title: '',
                og_description: '',
                og_image: '',
                twitter_title: '',
                twitter_description: '',
                twitter_image: '',
                keywords: '',
                robots: 'index,follow'
              };
            })
          ]);
          
          analytics = analyticsResult;
          links = linksResult || [];
          socialLinks = socialLinksResult || {};
          footerBranding = footerBrandingResult || { text: 'Hosted by Link Spaghetti ❤️' };
          seoSettings = seoSettingsResult || {
            title: '',
            description: '',
            og_title: '',
            og_description: '',
            og_image: '',
            twitter_title: '',
            twitter_description: '',
            twitter_image: '',
            keywords: '',
            robots: 'index,follow'
          };
          
          // Check if we're in offline mode for social links
          if (localStorage.getItem('socialLinksOffline') === 'true') {
            setIsOfflineMode(true);
          }
        } else {
          backendAvailable = false;
          setBackendAvailable(false);
          if (import.meta.env.DEV) {
            console.warn('Profile API not available');
          }
        }

        // Initialize default social links if empty
        if (Object.keys(socialLinks).length === 0) {
          socialLinks = {
            twitter: '',
            instagram: '',
            linkedin: '',
            github: '',
            youtube: '',
            tiktok: ''
          };
        }

      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Failed to load data:', error);
        }
        backendAvailable = false;
        setBackendAvailable(false);
        
        setError('Failed to connect to backend API. Please ensure the backend server is running.');
        setIsLoading(false);
        return;
      }

      // Also try to get enhanced analytics summary if available
      let enhancedAnalytics = null;
      if (backendAvailable) {
        try {
          enhancedAnalytics = await analyticsService.getAnalyticsSummary(7);
        } catch (error) {
          if (import.meta.env.DEV) {
            console.warn('Enhanced analytics not available:', error);
          }
        }
      }
      
      // Ensure social links have proper structure with default empty values
      const defaultSocialLinks = {
        twitter: '',
        instagram: '',
        linkedin: '',
        github: '',
        youtube: '',
        tiktok: ''
      };
      const formattedSocialLinks = { ...defaultSocialLinks, ...socialLinks };
      
      setUserData(prev => ({ 
        ...prev,
        ...data, 
        links: links || [], 
        socialLinks: formattedSocialLinks, 
        bio: data.bio || '', // Ensure bio is never null
        theme: {
          preset: data.theme?.preset || 'default',
          customCSS: data.theme?.customCSS || '',
          customColors: {
            primary: '#6366f1',
            secondary: '#ec4899',
            background: '#ffffff',
            ...data.theme?.customColors
          }
        },
        seo: {
          title: seoSettings?.title || data.seo?.title || '',
          description: seoSettings?.description || data.seo?.description || '',
          og_title: seoSettings?.og_title || data.seo?.og_title || '',
          og_description: seoSettings?.og_description || data.seo?.og_description || '',
          og_image: seoSettings?.og_image || data.seo?.og_image || '',
          twitter_title: seoSettings?.twitter_title || data.seo?.twitter_title || '',
          twitter_description: seoSettings?.twitter_description || data.seo?.twitter_description || '',
          twitter_image: seoSettings?.twitter_image || data.seo?.twitter_image || '',
          keywords: seoSettings?.keywords || data.seo?.keywords || '',
          robots: seoSettings?.robots || data.seo?.robots || 'index,follow',
          ...data.seo
        },
        footerBranding: footerBranding || {
          text: data.footerBranding?.text || 'Hosted by Link Spaghetti ❤️'
        }
      }));

      // Merge basic and enhanced analytics with proper data structure mapping
      const mergedAnalytics = {
        // Map API response fields to frontend expected fields
        totalViews: analytics?.totalViews || enhancedAnalytics?.totalViews || 0,
        totalVisitors: analytics?.totalVisitors || analytics?.uniqueVisitors || enhancedAnalytics?.uniqueVisitors || 0,
        totalPageViews: analytics?.totalPageViews || analytics?.totalViews || enhancedAnalytics?.totalViews || 0,
        totalClicks: analytics?.totalClicks || analytics?.linkClicks || enhancedAnalytics?.linkClicks || 0,
        avgTimeOnPage: analytics?.avgTimeOnPage || enhancedAnalytics?.avgTimeOnPage || '0:00',
        uniqueVisitors: analytics?.uniqueVisitors || enhancedAnalytics?.uniqueVisitors || 0,
        linkClicks: analytics?.linkClicks || enhancedAnalytics?.linkClicks || 0,
        conversionRate: analytics?.conversionRate || enhancedAnalytics?.conversionRate || 0,
        // Map arrays with fallbacks
        daily: (enhancedAnalytics?.daily) || (analytics?.daily) || (analytics?.dailyViews) || [],
        sources: Array.isArray(enhancedAnalytics?.sources) ? enhancedAnalytics.sources :
                Array.isArray(analytics?.sources) ? analytics.sources :
                Array.isArray(analytics?.referrers) ? analytics.referrers : [],
        devices: (enhancedAnalytics?.devices) || analytics?.devices || {},
        links: (enhancedAnalytics?.links) || analytics?.links || (analytics?.topPages) || [],
        geography: Array.isArray(enhancedAnalytics?.geography) ? enhancedAnalytics.geography :
                  Array.isArray(analytics?.geography) ? analytics.geography : []
      };

      if (import.meta.env.DEV) {
        console.log('📊 Raw analytics data:', analytics);
        console.log('📊 Enhanced analytics data:', enhancedAnalytics);
        console.log('📊 Merged analytics data:', mergedAnalytics);
      }

      setAnalyticsData(prev => ({
        ...prev,
        ...mergedAnalytics
      }));
      setAvailableThemes(THEME_PRESETS);
      
      // Debug theme loading
      if (import.meta.env.DEV) {
        console.log('🎨 All themes available to all users');
        console.log('🎨 Available themes:', THEME_PRESETS);
        console.log('🎨 Current theme preset:', data.theme?.preset);
      }
      
      // Set user ID for analytics tracking
      if (data.id) {
        analyticsService.setUserId(data.id);
      }
    } catch (error) {
      handleApiError(error, 'Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Check authentication and load data on component mount
  useEffect(() => {
    let isSubscribed = true;
    
    const initializeDashboard = async () => {
      const authStatus = isAuthenticated();
      
      if (!authStatus) {
        logout(); // Clean up any invalid tokens
        navigate('/signin', { replace: true });
        return;
      }
      
      // Only proceed with data loading if still subscribed
      if (isSubscribed) {
        await loadData();
      }
    };

    initializeDashboard();
    
    // Cleanup subscription on unmount
    return () => {
      isSubscribed = false;
      // Clean up any pending timeouts
      if (profileUpdateTimeout.current) {
        clearTimeout(profileUpdateTimeout.current);
      }
      if (socialLinksUpdateTimeout.current) {
        clearTimeout(socialLinksUpdateTimeout.current);
      }
      if (themeUpdateTimeout.current) {
        clearTimeout(themeUpdateTimeout.current);
      }
      if (footerBrandingUpdateTimeout.current) {
        clearTimeout(footerBrandingUpdateTimeout.current);
      }
      if (seoUpdateTimeout.current) {
        clearTimeout(seoUpdateTimeout.current);
      }
    };
  }, [navigate]); // Keep navigate in deps since it's stable and required

  // All themes available to all users
  useEffect(() => {
    setAvailableThemes(THEME_PRESETS);
  }, []);

  const [newLink, setNewLink] = useState({ title: '', url: '' });
  const [draggedLinkId, setDraggedLinkId] = useState(null);
  const [dragOverLinkId, setDragOverLinkId] = useState(null);

  // Drag and drop handlers for reordering links
  const handleDragStart = (e, linkId) => {
    setDraggedLinkId(linkId);
    e.dataTransfer.effectAllowed = 'move';
    // Add a small delay to ensure the drag image is visible
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedLinkId(null);
    setDragOverLinkId(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, linkId) => {
    e.preventDefault();
    if (draggedLinkId && draggedLinkId !== linkId) {
      setDragOverLinkId(linkId);
    }
  };

  const handleDragLeave = (e) => {
    // Only clear dragOver if we're actually leaving the element
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverLinkId(null);
    }
  };

  const handleDrop = async (e, targetLinkId) => {
    e.preventDefault();
    setDragOverLinkId(null);
    
    if (draggedLinkId === targetLinkId) return;
    
    const links = [...userData.links].sort((a, b) => (a.order || 0) - (b.order || 0));
    const draggedIndex = links.findIndex(link => link.id === draggedLinkId);
    const targetIndex = links.findIndex(link => link.id === targetLinkId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    // Remove the dragged item
    const [draggedItem] = links.splice(draggedIndex, 1);
    // Insert it at the target position
    links.splice(targetIndex, 0, draggedItem);
    
    // Update the order property for all links
    const reorderedLinks = links.map((link, index) => ({
      ...link,
      order: index
    }));
    
    // Update UI immediately for better UX
    setUserData({ ...userData, links: reorderedLinks });
    
    try {
      // Note: Link reordering API is not available in the backend yet
      // This will only update the local state
      showSuccess('Links reordered locally (backend API not available)');
    } catch (error) {
      // Revert the UI changes on error
      setUserData({ ...userData, links: [...userData.links] });
      handleApiError(error, 'Failed to reorder links. Please try again.');
    }
  };

  // Add refs for debouncing
  const socialLinksUpdateTimeout = useRef(null);
  const profileUpdateTimeout = useRef(null);
  const themeUpdateTimeout = useRef(null);
  const footerBrandingUpdateTimeout = useRef(null);

  const handleProfileUpdate = async (field, value) => {
    // Add debug logging for image uploads
    if (field === 'profileImage' || field === 'bannerImage') {
      console.log('🖼️ Image update requested:', { 
        field, 
        valueLength: value ? value.length : 0,
        isBase64: value ? value.startsWith('data:image') : false
      });
    }
    
    try {
      // Update UI immediately
      const updatedData = { ...userData, [field]: value };
      setUserData(updatedData);
      
      // Debounce the API call
      if (profileUpdateTimeout.current) {
        clearTimeout(profileUpdateTimeout.current);
      }
      
      profileUpdateTimeout.current = setTimeout(async () => {
        try {
          setIsSaving(true);
          if (import.meta.env.DEV) {
            console.log('🔄 Sending profile update to API:', { field, valuePreview: value ? `${value.substring(0, 50)}...` : null });
          }
          const updateResult = await updateUserProfile({ [field]: value });
          if (import.meta.env.DEV) {
            console.log('✅ Profile update API response:', updateResult);
          }
          
          // For image uploads, let's refetch the user profile to see what the backend saved
          if (field === 'profileImage' || field === 'bannerImage') {
            console.log('🔄 Refetching user profile after image update...');
            try {
              const updatedProfile = await getUserProfile();
              console.log('📊 Updated profile from backend:', { 
                profileImage: updatedProfile.profileImage ? `Present (${updatedProfile.profileImage.substring(0, 50)}...)` : 'null',
                bannerImage: updatedProfile.bannerImage ? `Present (${updatedProfile.bannerImage.substring(0, 50)}...)` : 'null',
                profile_image: updatedProfile.profile_image ? `Present (${updatedProfile.profile_image.substring(0, 50)}...)` : 'null',
                banner_image: updatedProfile.banner_image ? `Present (${updatedProfile.banner_image.substring(0, 50)}...)` : 'null'
              });
              setUserData(prev => ({ ...prev, ...updatedProfile }));
            } catch (refetchError) {
              console.error('❌ Failed to refetch profile:', refetchError);
            }
          }
          
          // Only show success message for important fields
          if (['name', 'username', 'profileImage', 'bannerImage'].includes(field) && import.meta.env.DEV) {
            showSuccess(`${field === 'profileImage' ? 'Profile image' : field === 'bannerImage' ? 'Banner image' : 'Profile'} updated successfully!`);
          }
        } catch (error) {
          handleApiError(error, 'Failed to update profile. Please try again.');
        } finally {
          setIsSaving(false);
        }
      }, 2000); // 2 second debounce for profile updates
    } catch (error) {
      handleApiError(error, 'Failed to update profile. Please try again.');
    }
  };

  
  const handleSocialLinkUpdate = async (platform, value) => {
    try {
      const updatedSocialLinks = { ...userData.socialLinks, [platform]: value };
      setUserData({ ...userData, socialLinks: updatedSocialLinks });
      
      if (import.meta.env.DEV) {
        console.log(`🔄 Social link update for ${platform}:`, value);
      }
      
      // Debounce the API call to avoid too many requests
      if (socialLinksUpdateTimeout.current) {
        clearTimeout(socialLinksUpdateTimeout.current);
      }
      
      socialLinksUpdateTimeout.current = setTimeout(async () => {
        try {
          setIsSaving(true);
          const result = await updateSocialLinks(updatedSocialLinks);
          
          if (result.offline) {
            if (import.meta.env.DEV) {
              showSuccess('Social links saved offline! They will sync when the backend is available.');
              console.warn('⚠️ Social links saved offline due to backend unavailability');
            }
          } else {
            if (import.meta.env.DEV) {
              showSuccess('Social links saved successfully!');
              console.log('✅ Social links saved to backend');
            }
          }
        } catch (error) {
          console.error('❌ Error saving social links:', error);
          
          // Check if it's a connectivity issue
          if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
            showError('Backend is unavailable. Changes saved locally and will sync when online.');
            
            // Save locally as fallback
            const localSocialLinks = { ...updatedSocialLinks };
            localStorage.setItem('socialLinks', JSON.stringify(localSocialLinks));
            localStorage.setItem('socialLinksOffline', 'true');
          } else {
            showError('Failed to save social media links. Please try again.');
            
            // Revert the local state on error
            setUserData({ ...userData, socialLinks: userData.socialLinks });
          }
        } finally {
          setIsSaving(false);
        }
      }, 2000); // 2 second debounce for social links
    } catch (error) {
      console.error('❌ Error updating social link:', error);
      showError('Failed to update social media link');
    }
  };

  const handleAddLink = async () => {
    if (!newLink.title || !newLink.url) return;
    try {
      setIsSaving(true);
      const created = await createLink(newLink);
      setUserData({ ...userData, links: [...userData.links, created] });
      setNewLink({ title: '', url: '' });
      showSuccess('Link added successfully!');
    } catch (error) {
      console.error('❌ Add link failed:', error);
      
      // Check for specific error types and provide appropriate messaging
      if (error.message && error.message.includes('Server error')) {
        showError('Unable to add link: Server is experiencing issues. The backend team has been notified. Please try again later.');
      } else if (error.message && error.message.includes('CORS')) {
        showError('Unable to add link: Backend CORS configuration issue. Please contact support or try again later.');
      } else if (error.message && error.message.includes('Failed to fetch')) {
        showError('Unable to add link: Network connection issue. Please check your internet connection and try again.');
      } else {
        handleApiError(error, 'Failed to add link.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateLink = async (id, data) => {
    try {
      setIsSaving(true);
      const updated = await updateLink(id, data);
      setUserData({ ...userData, links: userData.links.map(l => l.id === id ? updated : l) });
      showSuccess('Link updated successfully!');
    } catch (error) {
      handleApiError(error, 'Failed to update link.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteLink = async (id) => {
    try {
      // Validate id to prevent 400 errors
      if (!id) {
        console.error('Attempted to delete link with undefined id');
        showError('Cannot delete link: Missing ID');
        return;
      }
      
      setIsSaving(true);
      await deleteLink(id);
      setUserData({ ...userData, links: userData.links.filter(l => l.id !== id) });
      showSuccess('Link deleted successfully!');
    } catch (error) {
      handleApiError(error, 'Failed to delete link.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = (field) => {
    // In real app, this would handle file upload
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          handleProfileUpdate(field, e.target.result);
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const menuItems = [
    { id: 'profile', label: 'Profile', icon: '👤' },
    { id: 'links', label: 'Links', icon: '🔗' },
    { id: 'appearance', label: 'Appearance', icon: '🎨' },
    { id: 'analytics', label: 'Analytics', icon: '📊' },
    { id: 'support', label: 'Support', icon: '🎫' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  // Tier badge styles
  const tierBadges = {
    free: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Free' },
    pro: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Pro' },
    max: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Max' },
  };

  // Logout handler
  const handleLogout = () => {
    logout(); // Use the new logout utility function
    navigate('/signin', { replace: true });
  };

  // Handle theme updates
  const updateTheme = async (themeData) => {
    try {
      setIsSaving(true);
      
      // Debug theme update
      if (import.meta.env.DEV) {
        console.log('🎨 Updating theme:', themeData);
        console.log('🎨 Current userData.theme:', userData.theme);
      }
      
      // Debounce the API call to prevent spam
      if (themeUpdateTimeout.current) {
        clearTimeout(themeUpdateTimeout.current);
      }
      
      themeUpdateTimeout.current = setTimeout(async () => {
        try {
          // Use the dedicated theme API
          await updateUserTheme(themeData);
          if (import.meta.env.DEV) {
            console.log('🎨 Theme successfully saved to backend');
          }
          // Only show success message for significant changes (not color picker drags)
        } catch (error) {
          showError('Failed to update theme');
          console.error('Theme update error:', error);
        }
      }, 1000); // 1 second debounce for theme updates
      
    } catch (error) {
      showError('Failed to update theme');
      console.error('Theme update error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle immediate theme UI updates without API calls
  const updateThemeUI = (themeData) => {
    // Update UI immediately for better UX
    setUserData(prev => ({
      ...prev,
      theme: themeData
    }));
    
    // Debounce the API call
    updateTheme(themeData);
  };

  // Handle SEO settings updates with debouncing
  const seoUpdateTimeout = useRef(null);
  
  const handleSeoSettingsUpdate = async (seoData) => {
    try {
      // Update UI immediately
      setUserData(prev => ({
        ...prev,
        seo: { ...prev.seo, ...seoData }
      }));

      // Clear existing timeout
      if (seoUpdateTimeout.current) {
        clearTimeout(seoUpdateTimeout.current);
      }

      // Debounce the API call
      seoUpdateTimeout.current = setTimeout(async () => {
        try {
          await updateSeoSettings(seoData);
          if (import.meta.env.DEV) {
            console.log('📝 SEO settings successfully saved to backend');
          }
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('Failed to save SEO settings:', error);
          }
          // Show error notification
          showError('Failed to save SEO settings. Please try again.');
        }
      }, 2000); // 2 second delay
    } catch (error) {
      console.error('❌ Error updating SEO settings:', error);
      showError('Failed to update SEO settings');
    }
  };

  const handleSeoSettingsReset = async () => {
    try {
      const defaultSettings = await resetSeoSettings();
      setUserData(prev => ({
        ...prev,
        seo: defaultSettings
      }));
      if (import.meta.env.DEV) {
        console.log('📝 SEO settings reset to defaults');
      }
    } catch (error) {
      console.error('❌ Error resetting SEO settings:', error);
      showError('Failed to reset SEO settings');
    }
  };

  // Handle footer branding updates with debouncing
  const handleFooterBrandingUpdate = async (text) => {
    try {
      // Update UI immediately
      const updatedData = {
        ...userData,
        footerBranding: { text }
      };
      setUserData(updatedData);
      
      // Debounce the API call
      if (footerBrandingUpdateTimeout.current) {
        clearTimeout(footerBrandingUpdateTimeout.current);
      }
      
      footerBrandingUpdateTimeout.current = setTimeout(async () => {
        try {
          await updateFooterBranding(text);
          if (import.meta.env.DEV) {
            console.log('✅ Footer branding updated:', text);
          }
          showSuccess('Footer branding updated successfully');
        } catch (error) {
          console.error('❌ Error updating footer branding:', error);
          // Revert UI on error
          setUserData(userData);
          showError('Failed to update footer branding');
        }
      }, 2000); // 2 second debounce for footer branding
    } catch (error) {
      console.error('❌ Error updating footer branding:', error);
      showError('Failed to update footer branding');
    }
  };

  // Handle footer branding reset
  const handleFooterBrandingReset = async () => {
    try {
      const defaultBranding = await resetFooterBranding();
      const updatedData = {
        ...userData,
        footerBranding: defaultBranding
      };
      setUserData(updatedData);
      showSuccess('Footer branding reset to default');
    } catch (error) {
      console.error('❌ Error resetting footer branding:', error);
      showError('Failed to reset footer branding');
    }
  };

  // Apply theme on mount and when it changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  if (isLoading) {
    return <LoadingPage message="Loading your dashboard..." />;
  }

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto text-center">
          <div className="bg-white shadow-lg rounded-lg p-8">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-red-100 rounded-full">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Backend API Not Available</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">For Developers:</h3>
                <div className="text-sm text-blue-800 space-y-1">
                  <p>1. Start the backend server</p>
                  <p>2. Ensure it's running on <code className="bg-blue-100 px-1 rounded">http://127.0.0.1:5000</code></p>
                  <p>3. Verify the <code className="bg-blue-100 px-1 rounded">/api/user</code> endpoint is implemented</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button 
                  onClick={() => window.location.reload()} 
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry Connection
                </button>
                <Link 
                  to="/" 
                  className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-center"
                >
                  Go Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!userData.id) {
    return <LoadingPage />;
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'themed' : 'bg-gray-50'}`}>
      <ToastContainer />
      
      {/* Email Verification Banner */}
      <EmailVerificationBanner 
        user={userData} 
        onVerificationChange={(verified) => {
          if (verified && userData) {
            setUserData(prev => ({ ...prev, email_verified: true }));
          }
        }} 
      />
      
      {/* Offline Mode Banner */}
      {isOfflineMode && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm">
                <strong>Offline Mode:</strong> Your changes are being saved locally. They will sync when the backend is available.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Top Navigation */}
      <nav className={`${theme === 'dark' ? 'themed-secondary themed-border' : 'bg-white'} border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} px-4 sm:px-6 lg:px-8 sticky top-0 z-30`}>
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-4">
            <MobileMenuButton onClick={() => setIsMobileMenuOpen(true)} theme={theme} />
            <Link to="/" className={`text-xl sm:text-2xl font-bold ${theme === 'dark' ? 'text-transparent bg-gradient-to-r from-[#2980b9] to-[#16a085] bg-clip-text' : 'bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent'}`}>
              Link Spaghetti
            </Link>
            <span className={`hidden sm:inline-flex px-2 py-1 text-xs font-medium rounded-full ${tierBadges[userData.tier].bg} ${tierBadges[userData.tier].text}`}>
              {tierBadges[userData.tier].label}
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to={`/${userData.username}?preview=true&tab=${activeTab}`} className={`${theme === 'dark' ? 'themed-text-secondary hover:text-white' : 'text-gray-600 hover:text-gray-900'} flex items-center gap-1 sm:gap-2 text-sm sm:text-base`}>
              <span className="hidden xs:inline">Preview</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </Link>
            <LoadingButton
              loading={isSaving}
              className={`${theme === 'dark' ? 'bg-gradient-to-r from-[#2980b9] to-[#16a085]' : 'button-gradient'} text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm transition-all hover:shadow-lg`}
            >
              <span className="hidden xs:inline">Publish</span>
              <span className="xs:hidden">Save</span>
            </LoadingButton>
            {userData.tier === 'free' && (
              <Link to="/upgrade" className="hidden sm:inline text-sm font-medium text-purple-600 hover:text-purple-700">
                Upgrade ✨
              </Link>
            )}
            <button
              onClick={handleLogout}
              className={`hidden sm:inline ${theme === 'dark' ? 'themed-text-secondary hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} theme={theme}>
        <div className="space-y-1 mt-8">
          {menuItems.map((item) => (
            <button
              key={`mobile-${item.id}`}
              onClick={() => {
                setActiveTab(item.id);
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-all ${
                activeTab === item.id
                  ? theme === 'dark' 
                    ? 'bg-[#2980b9]/20 text-[#2980b9]'
                    : 'bg-purple-50 text-purple-600'
                  : theme === 'dark'
                    ? 'themed-text-secondary hover:bg-gray-700'
                    : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="text-xl">{item.icon}</span>
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
          <div className={`pt-4 mt-4 ${theme === 'dark' ? 'border-t border-gray-600' : 'border-t border-gray-200'}`}>
            {userData.tier === 'free' && (
              <Link to="/upgrade" className={`block w-full text-center ${theme === 'dark' ? 'bg-gradient-to-r from-[#2980b9] to-[#16a085]' : 'bg-purple-600'} text-white px-4 py-2 rounded-lg mb-3 hover:opacity-90 transition-opacity`}>
                Upgrade to Pro ✨
              </Link>
            )}
            <button
              onClick={handleLogout}
              className={`block w-full text-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
            >
              Logout
            </button>
          </div>
        </div>
      </MobileMenu>

      <div className="flex">
        {/* Sidebar - Hidden on mobile */}
        <aside className={`hidden lg:block w-64 ${theme === 'dark' ? 'themed-secondary' : 'bg-white'} h-[calc(100vh-4rem)] border-r ${theme === 'dark' ? 'themed-border border-gray-700' : 'border-gray-200'} sticky top-16`}>
          <div className="p-4">
            <div className={`text-sm ${theme === 'dark' ? 'themed-text-tertiary' : 'text-gray-500'} mb-4`}>MANAGE</div>
            <nav className="space-y-1">
              {menuItems.map((item, index) => (
                <button
                  key={`desktop-${item.id}`}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all duration-200 ${
                    activeTab === item.id
                      ? theme === 'dark' 
                        ? 'bg-[#2980b9]/20 text-[#2980b9]'
                        : 'bg-purple-50 text-purple-600'
                      : theme === 'dark'
                        ? 'themed-text-secondary hover:bg-gray-700'
                        : 'text-gray-700 hover:bg-gray-50'
                  } animate-fade-in`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <span className="text-xl">{item.icon}</span>
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="max-w-4xl mx-auto animate-fade-in-up">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-8">
                <div>
                  <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>Profile</h2>
                  
                  {/* User Image Uploader */}
                  <div className="mb-8">
                    <UserImageUploader 
                      userId={userData.id}
                      currentImages={{
                        profile: userData.profileImage,
                        banner: userData.bannerImage
                      }}
                      onImageUpdate={(fieldName, imageUrl) => {
                        // Update the UI state directly with the new image URL
                        if (import.meta.env.DEV) {
                          console.log('🖼️ Image upload completed:', { fieldName, imageUrl });
                        }
                        setUserData(prev => {
                          const updated = {
                            ...prev,
                            [fieldName]: imageUrl
                          };
                          if (import.meta.env.DEV) {
                            console.log('🔄 Updated userData:', { 
                              before: { profileImage: prev.profileImage, bannerImage: prev.bannerImage },
                              after: { profileImage: updated.profileImage, bannerImage: updated.bannerImage }
                            });
                          }
                          return updated;
                        });
                      }}
                      theme={theme}
                    />
                  </div>

                  {/* Profile Info */}
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'} mb-1`}>
                        Display Name
                      </label>
                      <input
                        type="text"
                        value={userData.name}
                        onChange={(e) => handleProfileUpdate('name', e.target.value)}
                        className={`w-full px-3 py-2 border ${theme === 'dark' ? 'themed-tertiary themed-border border-gray-600 text-white placeholder-gray-400 focus:border-[#2980b9]' : 'border-gray-300 focus:border-purple-500'} rounded-lg focus:outline-none focus:ring-1 ${theme === 'dark' ? 'focus:ring-[#2980b9]' : 'focus:ring-purple-500'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'} mb-1`}>
                        Username
                      </label>
                      <div className="flex">
                        <span className={`inline-flex items-center px-3 rounded-l-lg border border-r-0 ${theme === 'dark' ? 'themed-tertiary themed-border border-gray-600 themed-text-tertiary' : 'border-gray-300 bg-gray-50 text-gray-500'} text-sm`}>
                          linkspaghetti.com/
                        </span>
                        <input
                          type="text"
                          value={userData.username}
                          onChange={(e) => handleProfileUpdate('username', e.target.value)}
                          className={`flex-1 px-3 py-2 border ${theme === 'dark' ? 'themed-tertiary themed-border border-gray-600 text-white placeholder-gray-400 focus:border-[#2980b9]' : 'border-gray-300 focus:border-purple-500'} rounded-r-lg focus:outline-none focus:ring-1 ${theme === 'dark' ? 'focus:ring-[#2980b9]' : 'focus:ring-purple-500'}`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'} mb-1`}>
                        Bio
                      </label>
                      <textarea
                        value={userData.bio}
                        onChange={(e) => handleProfileUpdate('bio', e.target.value)}
                        rows={3}
                        className={`w-full px-3 py-2 border ${theme === 'dark' ? 'themed-tertiary themed-border border-gray-600 text-white placeholder-gray-400 focus:border-[#2980b9]' : 'border-gray-300 focus:border-purple-500'} rounded-lg focus:outline-none focus:ring-1 ${theme === 'dark' ? 'focus:ring-[#2980b9]' : 'focus:ring-purple-500'}`}
                        placeholder="Tell visitors about yourself..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Links Tab */}
            {activeTab === 'links' && (
              <div className="space-y-8">
                <div>
                  <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>Links</h2>
                  
                  {/* Social Links */}
                  <div className="mb-8">
                    <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Social Links</h3>
                    <div className="space-y-3">
                      {Object.entries(userData.socialLinks || {})
                        .filter(([platform]) => platform) // Filter out null/undefined platforms
                        .map(([platform, url]) => (
                          <div key={`social-${platform}`} className="flex items-center gap-3">
                            <label className={`w-24 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{platform}</label>
                            <input
                              type="url"
                              value={url || ''}
                              onChange={(e) => handleSocialLinkUpdate(platform, e.target.value)}
                              placeholder={`Your ${platform} URL`}
                              className={`flex-1 px-3 py-2 border ${theme === 'dark' ? 'themed-tertiary themed-border border-gray-600 text-white placeholder-gray-400 focus:border-[#2980b9]' : 'border-gray-300 focus:border-purple-500'} rounded-lg focus:outline-none focus:ring-1 ${theme === 'dark' ? 'focus:ring-[#2980b9]' : 'focus:ring-purple-500'}`}
                            />
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Custom Links */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Custom Links</h3>
                      {userData.links.length > 1 && (
                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} flex items-center gap-1`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                          </svg>
                          Drag to reorder
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      {(userData.links || [])
                        .sort((a, b) => ((typeof a.order === 'number' ? a.order : 0) - (typeof b.order === 'number' ? b.order : 0)))
                        .map((link, index) => (
                        <div 
                          key={link.id || `link-${index}`} 
                          className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-200 ${
                            theme === 'dark' ? 'themed-tertiary' : 'bg-gray-50'
                          } ${
                            draggedLinkId === link.id ? 'opacity-50 scale-95' : ''
                          } ${
                            dragOverLinkId === link.id ? 
                              `ring-2 ${theme === 'dark' ? 'ring-[#2980b9]' : 'ring-purple-500'} scale-105` : ''
                          } cursor-move hover:shadow-md group`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, link.id)}
                          onDragEnd={handleDragEnd}
                          onDragOver={handleDragOver}
                          onDragEnter={(e) => handleDragEnter(e, link.id)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, link.id)}
                        >
                          {/* Drag Handle */}
                          <div className={`${theme === 'dark' ? 'text-gray-400 group-hover:text-[#2980b9]' : 'text-gray-500 group-hover:text-purple-600'} cursor-move transition-colors`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                            </svg>
                          </div>
                          
                          <div className="flex-1">
                            <input
                              type="text"
                              value={link.title}
                              onChange={(e) => {
                                const updatedLinks = userData.links.map(l =>
                                  l.id === link.id ? { ...l, title: e.target.value } : l
                                );
                                setUserData({ ...userData, links: updatedLinks });
                              }}
                              className={`w-full px-3 py-2 mb-2 border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} rounded focus:outline-none focus:ring-2 ${theme === 'dark' ? 'focus:ring-[#2980b9] focus:border-[#2980b9]' : 'focus:ring-purple-500 focus:border-purple-500'}`}
                              placeholder="Link title"
                            />
                            <input
                              type="url"
                              value={link.url}
                              onChange={(e) => {
                                const updatedLinks = userData.links.map(l =>
                                  l.id === link.id ? { ...l, url: e.target.value } : l
                                );
                                setUserData({ ...userData, links: updatedLinks });
                              }}
                              className={`w-full px-3 py-2 border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} rounded focus:outline-none focus:ring-2 ${theme === 'dark' ? 'focus:ring-[#2980b9] focus:border-[#2980b9]' : 'focus:ring-purple-500 focus:border-purple-500'}`}
                              placeholder="URL"
                            />
                          </div>
                          <button
                            onClick={() => {
                              console.log('🗑️ Delete button clicked for link:', link);
                              if (link.id || link._id) {
                                const linkId = link.id || link._id;
                                console.log('✅ Link ID found:', linkId);
                                handleDeleteLink(linkId);
                              } else {
                                console.error('❌ Link missing ID:', link);
                                showError('Cannot delete link: Missing ID. Please refresh the page and try again.');
                              }
                            }}
                            className={`${theme === 'dark' ? 'text-[#c0392b] hover:text-red-400' : 'text-red-600 hover:text-red-700'} transition-colors`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                      
                      {userData.links.length === 0 && (
                        <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          <p>No custom links yet. Add your first link below!</p>
                        </div>
                      )}
                      
                      {/* Add New Link */}
                      <div className={`flex gap-3 p-3 border-2 border-dashed ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'} rounded-lg`}>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={newLink.title}
                            onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                            className={`w-full px-3 py-2 mb-2 border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} rounded focus:outline-none focus:ring-2 ${theme === 'dark' ? 'focus:ring-[#2980b9] focus:border-[#2980b9]' : 'focus:ring-purple-500 focus:border-purple-500'}`}
                            placeholder="Link title"
                          />
                          <input
                            type="url"
                            value={newLink.url}
                            onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                            className={`w-full px-3 py-2 border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} rounded focus:outline-none focus:ring-2 ${theme === 'dark' ? 'focus:ring-[#2980b9] focus:border-[#2980b9]' : 'focus:ring-purple-500 focus:border-purple-500'}`}
                            placeholder="URL"
                          />
                        </div>
                        <button
                          onClick={handleAddLink}
                          disabled={!newLink.title || !newLink.url}
                          className={`${
                            !newLink.title || !newLink.url 
                              ? 'bg-gray-400 cursor-not-allowed' 
                              : theme === 'dark' 
                                ? 'bg-gradient-to-r from-[#2980b9] to-[#16a085] hover:from-[#3498db] hover:to-[#1abc9c]' 
                                : 'button-gradient hover:opacity-90'
                          } text-white px-4 py-2 rounded-lg transition-all`}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Appearance Tab */}
            {activeTab === 'appearance' && (
              <div className="space-y-8">
                <div>
                  <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>Appearance</h2>
                  
                  {/* Theme Presets */}
                  <div className={`${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'}`}>
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>Profile Themes</h3>
                        <p className={`${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'}`}>
                          Choose a theme for your public profile page. {userData.tier === 'free' && 'Upgrade to Pro for more themes!'}
                        </p>
                      </div>
                      {userData.tier === 'free' && (
                        <Link to="/upgrade" className="text-sm font-medium text-purple-600 hover:text-purple-700">
                          Upgrade →
                        </Link>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {availableThemes.map((themePreset) => (
                        <button
                          key={themePreset.id}
                          onClick={() => {
                            const updatedTheme = { 
                              ...userData.theme, 
                              preset: themePreset.id,
                              // Also include the colors from the preset
                              customColors: {
                                ...userData.theme?.customColors,
                                primary: themePreset.colors.primary,
                                secondary: themePreset.colors.secondary,
                                background: themePreset.colors.background
                              }
                            };
                            const updatedData = {
                              ...userData,
                              theme: updatedTheme
                            };
                            setUserData(updatedData);
                            updateTheme(updatedTheme);
                          }}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            userData.theme?.preset === themePreset.id
                              ? 'border-purple-500 bg-purple-50' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-2xl">{themePreset.preview}</span>
                            <span className={`font-medium ${userData.theme?.preset === themePreset.id ? 'text-purple-700' : 'text-gray-900'}`}>
                              {themePreset.name}
                            </span>
                          </div>
                          <div className="aspect-video rounded overflow-hidden border border-gray-200">
                            <div style={{ backgroundColor: themePreset.colors.background }} className="p-2 h-full">
                              <div style={{ backgroundColor: themePreset.colors.primary }} className="h-2 rounded mb-2"></div>
                              <div style={{ backgroundColor: themePreset.colors.linkBg, color: themePreset.colors.linkText }} className="h-4 rounded text-xs flex items-center px-1">
                                Link
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Pro/Max customization options */}
                    {userData.tier !== 'free' && (
                      <div className="border-t pt-6">
                        <h4 className={`text-md font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                          Customize Colors {userData.tier === 'pro' && '(Limited)'}
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'} mb-1`}>
                              Primary Color
                            </label>
                            <input
                              type="color"
                              value={userData.theme?.customColors?.primary || '#6366f1'}
                              onChange={(e) => {
                                const updatedTheme = {
                                  ...userData.theme,
                                  customColors: { 
                                    ...userData.theme?.customColors, 
                                    primary: e.target.value 
                                  }
                                };
                                updateThemeUI(updatedTheme);
                              }}
                              className="w-full h-10 rounded cursor-pointer"
                            />
                          </div>
                          <div>
                            <label className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'} mb-1`}>
                              Secondary Color
                            </label>
                            <input
                              type="color"
                              value={userData.theme?.customColors?.secondary || '#ec4899'}
                              onChange={(e) => {
                                const updatedTheme = {
                                  ...userData.theme,
                                  customColors: { 
                                    ...userData.theme?.customColors, 
                                    secondary: e.target.value 
                                  }
                                };
                                updateThemeUI(updatedTheme);
                              }}
                              className="w-full h-10 rounded cursor-pointer"
                            />
                          </div>
                          {(userData.tier === 'pro' || userData.tier === 'max') && (
                            <div className="col-span-2">
                              <label className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'} mb-1`}>
                                Background Color
                              </label>
                              <input
                                type="color"
                                value={userData.theme?.customColors?.background || '#ffffff'}
                                onChange={(e) => {
                                  const updatedTheme = {
                                    ...userData.theme,
                                    customColors: { 
                                      ...userData.theme?.customColors, 
                                      background: e.target.value 
                                    }
                                  };
                                  updateThemeUI(updatedTheme);
                                }}
                                className="w-full h-10 rounded cursor-pointer"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Max tier custom CSS */}
                    {userData.tier === 'max' && (
                      <div className="border-t pt-6 mt-6">
                        <h4 className={`text-md font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                          Custom CSS
                        </h4>
                        <textarea
                          value={userData.theme?.customCSS || ''}
                          onChange={(e) => {
                            const updatedTheme = {
                              ...userData.theme, 
                              customCSS: e.target.value 
                            };
                            updateThemeUI(updatedTheme);
                          }}
                          rows={6}
                          className={`w-full px-3 py-2 border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} rounded-lg font-mono text-sm`}
                          placeholder="/* Add your custom CSS here */"
                        />
                      </div>
                    )}
                  </div>

                  {/* Dashboard Theme */}
                  <div className={`mt-8 ${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'}`}>
                    <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Dashboard Theme</h3>
                    <p className={`${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'} mb-6`}>
                      Choose how your dashboard looks to you. This won't affect your public profile.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => setTheme('light')}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          theme === 'light' 
                            ? 'border-purple-500 bg-purple-50' 
                            : theme === 'dark'
                              ? 'border-gray-600 hover:border-gray-500'
                              : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-6 h-6 rounded-full bg-yellow-400"></div>
                          <span className={`font-medium ${theme === 'light' ? 'text-purple-700' : theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Light
                          </span>
                        </div>
                      </button>

                      <button
                        onClick={() => setTheme('dark')}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          theme === 'dark' 
                            ? 'border-[#2980b9] bg-[#2980b9]/10' 
                            : theme === 'dark'
                              ? 'border-gray-600 hover:border-gray-500'
                              : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-6 h-6 rounded-full bg-gray-700"></div>
                          <span className={`font-medium ${theme === 'dark' ? 'text-[#2980b9]' : theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Dark
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="space-y-8">
                <div>
                  <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>Analytics</h2>
                  
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className={`${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'}`}>
                      <div className={`text-sm ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'} mb-1`}>Total Visitors</div>
                      <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{analyticsData.totalVisitors}</div>
                    </div>
                    <div className={`${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'}`}>
                      <div className={`text-sm ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'} mb-1`}>Page Views</div>
                      <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{analyticsData.totalPageViews}</div>
                    </div>
                    <div className={`${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'}`}>
                      <div className={`text-sm ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'} mb-1`}>Avg Time</div>
                      <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{analyticsData.avgTimeOnPage}</div>
                    </div>
                  </div>

                  {/* Daily Visitors Chart (Free tier gets 5 days) */}
                  <div className={`${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'} mb-8`}>
                    <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                      Daily Visitors {userData.tier === 'free' && '(Last 5 days)'}
                    </h3>
                    <div className="space-y-2">
                      {(analyticsData.daily || []).map((day, index) => (
                        <div key={`day-${day.date}-${index}`} className="flex items-center gap-4">
                          <div className={`text-sm ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'} w-24`}>
                            {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </div>
                          <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                            <div 
                              className="absolute inset-y-0 left-0 bg-purple-500 rounded-full"
                              style={{ width: `${(day.visitors / Math.max(...analyticsData.daily.map(d => d.visitors))) * 100}%` }}
                            />
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                              {day.visitors}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {userData.tier === 'free' && (
                      <div className="mt-4 text-center">
                        <Link to="/upgrade" className="text-sm font-medium text-purple-600 hover:text-purple-700">
                          Upgrade to Pro for 30-day analytics →
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Pro/Max Analytics */}
                  {userData.tier !== 'free' && (
                    <>
                      {/* Device Breakdown */}
                      <div className="grid grid-cols-2 gap-4 mb-8">
                        <div className={`${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'}`}>
                          <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Device Breakdown</h3>
                          <div className="space-y-3">
                            {Object.entries(analyticsData.devices || {}).map(([device, percentage], index) => (
                              <div key={`device-${device}-${index}`}>
                                <div className="flex justify-between mb-1">
                                  <span className={`text-sm ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'} capitalize`}>{device}</span>
                                  <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{percentage}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-500 h-2 rounded-full"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Traffic Sources */}
                        <div className={`${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'}`}>
                          <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Traffic Sources</h3>
                          <div className="space-y-2">
                            {(analyticsData.sources || []).map((source, index) => (
                              <div key={`source-${source.name}-${index}`} className="flex justify-between items-center">
                                <span className={`text-sm ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'}`}>{source.name}</span>
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{source.visitors}</span>
                                  <span className={`text-xs ${theme === 'dark' ? 'themed-text-tertiary' : 'text-gray-500'}`}>({source.percentage}%)</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Geographic Data */}
                      <div className={`${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'}`}>
                        <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Top Countries</h3>
                        <div className="space-y-2">
                          {(analyticsData.geography || []).map((country, index) => (
                            <div key={`country-${country.country}-${index}`} className="flex items-center justify-between">
                              <span className={`text-sm ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'}`}>{country.country}</span>
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{country.visitors}</span>
                                <span className={`text-xs ${theme === 'dark' ? 'themed-text-tertiary' : 'text-gray-500'}`}>({country.percentage}%)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Max tier features */}
                  {userData.tier === 'max' && (
                    <div className={`mt-8 ${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'}`}>
                      <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Export & Reports</h3>
                      <div className="space-y-3">
                        <button className={`w-full text-left px-4 py-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}>
                          <div className="flex items-center justify-between">
                            <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Export as CSV</span>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                        </button>
                        <button className={`w-full text-left px-4 py-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}>
                          <div className="flex items-center justify-between">
                            <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Schedule Email Reports</span>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 012 2z" />
                            </svg>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Support Tab */}
            {activeTab === 'support' && (
              <div className="space-y-8">
                <div>
                  <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>Support</h2>
                  
                  {/* Support Information */}
                  <div className={`${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'} mb-8`}>
                    <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>How can we help you?</h3>
                    
                    {/* Common Questions */}
                    <div className="mb-6">
                      <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3`}>Common Questions</h4>
                      <div className="space-y-2">
                        <details className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          <summary className="cursor-pointer hover:text-purple-600">How do I customize my profile theme?</summary>
                          <p className={`mt-2 pl-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Go to the Appearance tab to choose from all available themes.
                          </p>
                        </details>
                        <details className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          <summary className="cursor-pointer hover:text-purple-600">Can I track link analytics?</summary>
                          <p className={`mt-2 pl-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Yes! Check the Analytics tab to see detailed statistics about your profile views and link clicks.
                          </p>
                        </details>
                        <details className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          <summary className="cursor-pointer hover:text-purple-600">How do I add custom links?</summary>
                          <p className={`mt-2 pl-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Navigate to the Links tab and click "Add New Link" to create your custom links.
                          </p>
                        </details>
                      </div>
                    </div>
                  </div>
                  
                  {/* Ticket System */}
                  <TicketSystem theme={theme} />
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-8">
                <div>
                  <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>Settings</h2>
                  
                  {/* SEO Settings - Available to all users */}
                  <div className={`${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'} mb-8`}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>SEO Settings</h3>
                        <button
                          onClick={handleSeoSettingsReset}
                          className={`text-xs ${theme === 'dark' ? 'text-[#2980b9] hover:text-blue-400' : 'text-purple-600 hover:text-purple-700'} font-medium`}
                        >
                          Reset to Default
                        </button>
                      </div>
                      <p className={`text-sm ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'} mb-6`}>
                        Optimize how your profile appears in search results and when shared on social media.
                      </p>
                      
                      <div className="space-y-4">
                        {/* Basic SEO */}
                        <div>
                          <label className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'} mb-1`}>
                            Page Title <span className="text-xs text-gray-500">(60 chars max)</span>
                          </label>
                          <input
                            type="text"
                            maxLength={60}
                            value={userData.seo?.title || ''}
                            onChange={(e) => handleSeoSettingsUpdate({ title: e.target.value })}
                            placeholder={`${userData.username}'s Profile | Link Spaghetti`}
                            className={`w-full px-3 py-2 border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'} rounded-lg`}
                          />
                          <div className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            {(userData.seo?.title || '').length}/60 characters
                          </div>
                        </div>
                        
                        <div>
                          <label className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'} mb-1`}>
                            Meta Description <span className="text-xs text-gray-500">(160 chars max)</span>
                          </label>
                          <textarea
                            maxLength={160}
                            value={userData.seo?.description || ''}
                            onChange={(e) => handleSeoSettingsUpdate({ description: e.target.value })}
                            placeholder={`Check out ${userData.username}'s links and profile on Link Spaghetti`}
                            rows={3}
                            className={`w-full px-3 py-2 border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'} rounded-lg`}
                          />
                          <div className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            {(userData.seo?.description || '').length}/160 characters
                          </div>
                        </div>

                        <div>
                          <label className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'} mb-1`}>
                            Keywords <span className="text-xs text-gray-500">(200 chars max)</span>
                          </label>
                          <input
                            type="text"
                            maxLength={200}
                            value={userData.seo?.keywords || ''}
                            onChange={(e) => handleSeoSettingsUpdate({ keywords: e.target.value })}
                            placeholder="keyword1, keyword2, keyword3"
                            className={`w-full px-3 py-2 border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'} rounded-lg`}
                          />
                          <div className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            {(userData.seo?.keywords || '').length}/200 characters
                          </div>
                        </div>

                        {/* OpenGraph Settings */}
                        <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                          <h4 className={`text-md font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3`}>
                            Social Media (OpenGraph)
                          </h4>
                          
                          <div className="space-y-4">
                            <div>
                              <label className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'} mb-1`}>
                                Social Title <span className="text-xs text-gray-500">(60 chars max)</span>
                              </label>
                              <input
                                type="text"
                                maxLength={60}
                                value={userData.seo?.og_title || ''}
                                onChange={(e) => handleSeoSettingsUpdate({ og_title: e.target.value })}
                                placeholder="Custom title for social sharing"
                                className={`w-full px-3 py-2 border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'} rounded-lg`}
                              />
                            </div>
                            
                            <div>
                              <label className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'} mb-1`}>
                                Social Description <span className="text-xs text-gray-500">(160 chars max)</span>
                              </label>
                              <textarea
                                maxLength={160}
                                value={userData.seo?.og_description || ''}
                                onChange={(e) => handleSeoSettingsUpdate({ og_description: e.target.value })}
                                placeholder="Description shown when shared on social media"
                                rows={2}
                                className={`w-full px-3 py-2 border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'} rounded-lg`}
                              />
                            </div>

                            <div>
                              <label className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'} mb-1`}>
                                Social Share Image URL
                              </label>
                              <input
                                type="url"
                                value={userData.seo?.og_image || ''}
                                onChange={(e) => handleSeoSettingsUpdate({ og_image: e.target.value })}
                                placeholder="https://example.com/image.jpg"
                                className={`w-full px-3 py-2 border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'} rounded-lg`}
                              />
                              <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                Recommended: 1200x630px for best social media display
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                  {/* Footer Settings */}
                  <div className={`${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'} mb-8`}>
                    <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Footer Branding</h3>
                    <div className="space-y-4">
                      <p className={`${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'} text-sm`}>
                        Customize the footer text displayed on your profile page. Max 100 characters.
                      </p>
                      <div>
                        <label className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'} mb-1`}>
                          Footer Text
                        </label>
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={userData.footerBranding?.text || 'Hosted by Link Spaghetti ❤️'}
                            onChange={(e) => {
                              if (e.target.value.length <= 100) {
                                handleFooterBrandingUpdate(e.target.value);
                              }
                            }}
                            maxLength={100}
                            className={`w-full px-3 py-2 border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} rounded-lg`}
                            placeholder="Hosted by Link Spaghetti ❤️"
                          />
                          <div className="flex items-center justify-between">
                            <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                              {(userData.footerBranding?.text || 'Hosted by Link Spaghetti ❤️').length}/100 characters
                            </span>
                            <button
                              onClick={handleFooterBrandingReset}
                              className={`text-xs ${theme === 'dark' ? 'text-[#2980b9] hover:text-blue-400' : 'text-purple-600 hover:text-purple-700'} font-medium`}
                            >
                              Reset to Default
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Support Information */}
                  <div className={`${theme === 'dark' ? 'themed-tertiary' : 'bg-white'} p-6 rounded-lg ${theme === 'dark' ? 'themed-border border border-gray-700' : 'border border-gray-200'}`}>
                    <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Support</h3>
                    <div className={`${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'}`}>
                      <p>Email support available for all users</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;